import Input from 'components/Input/Input';
import { useDispatch, useSelector } from 'react-redux';
import { IRootReducerState } from 'store/store';
import { Modal } from '../Modal/Modal';
import {
  Column,
  Container,
  Divider,
  FirstRow,
  GridWrap,
  InputWrapper,
  PopularFlexWrap,
  PopularSection,
  ResultsSection,
  ScrollArea,
  SelectWrapper,
  Title,
} from './ColorModal.styled';
import { MagnifyingGlass as Search } from '@phosphor-icons/react';
import { getIn, useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import {
  extractColorCollectionOptions,
  extractColorsFromQuestion,
  filterColorsByCollectionAndSearchTerm,
  getIsColorSelected,
} from './helpers';
import { IColorModalReducerState } from 'store/ColorModal/reducers/colorModal';
import { closeColorModal } from 'store/ColorModal/actions/colorModal';
import { Select } from 'components/Select/Select';
import { IOption } from 'components/Select/type';
import ColorCard from './ColorCard/ColorCard';
import PopularColorCard from './PopularColorCard/PopularColorCard';
import { getChosenColorOptionFromQuestionOptions } from 'components/Input/ColorInput/helpers';
import { useSetManuallyChangeQuestionName } from 'pages/ConfigurePage/ConfigureForm/hooks';

export interface IColor {
  additional_price: string;
  collection: string;
  description: string;
  hex_code?: string;
  image?: string;
  name: string;
  popular: string;
  code: string;
}

export function ColorModal() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [selectedCollection, setSelectedCollection] = useState<string>('');
  const [colorSearchBy, setColorSearchBy] = useState<string>('');

  const { isOpen, question }: IColorModalReducerState = useSelector(
    (state: IRootReducerState) => state.colorModalInfo
  );
  const context = useFormikContext();

  // Reset local state when a different question opens the modal
  useEffect(() => {
    setSelectedCollection('');
    setColorSearchBy('');
  }, [question?.initialKey]);

  if (!question || !context) {
    return null; // Modal not opened
  }

  const setManuallyChangeQuestionName =
    useSetManuallyChangeQuestionName(dispatch);

  const handleChooseColor = (color: IColor) => {
    const colorValues = question.inputs.map(
      (key: string) => (color as unknown as Record<string, string>)[key]
    );
    context.setFieldValue(question.initialKey, colorValues);
    setManuallyChangeQuestionName(colorValues, question.initialKey);
    handleCancel();
  };
  const handleCancel = () => {
    setColorSearchBy('');
    dispatch(closeColorModal());
  };

  const chosenColorValue: string | undefined = getIn(
    context.values,
    question.initialKey
  );
  const chosenColor: IColor | undefined =
    getChosenColorOptionFromQuestionOptions(chosenColorValue, question);

  const extractedColors: IColor[] = extractColorsFromQuestion(question);
  const colorCollectionOptions: IOption[] =
    extractColorCollectionOptions(extractedColors);

  const colors: IColor[] = filterColorsByCollectionAndSearchTerm(
    extractedColors,
    selectedCollection,
    colorSearchBy
  );

  const popularColors: IColor[] = colors.filter(
    (color: IColor) => color.popular === 'true'
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => handleCancel()}
      modalStyle={{
        position: 'fixed',
        margin: 'auto',
        maxWidth: '1340rem',
        // Header row and close icon stay put, only ScrollArea scrolls
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }} // Center positioning
    >
      <Container>
        <FirstRow>
          <Column>
            <Title>{t('Find color by name or code')}</Title>
            <InputWrapper>
              <Input
                icon={Search}
                placeholder={t('Search')}
                width={'100%'}
                wrapperStyles={{ width: '100%' }}
                onChange={(e: any) => setColorSearchBy(e.target.value)}
                autoFocus
              />
            </InputWrapper>
          </Column>
          <Divider />
          <Column>
            <Title>{t('Select a color collection')}</Title>
            <SelectWrapper>
              <Select
                width={'100%'}
                name="collection-select"
                placeholder={t('Choose')}
                options={colorCollectionOptions}
                controlledValue={
                  colorCollectionOptions.find(
                    (opt) => opt.value === selectedCollection
                  ) || { label: t('All collections'), value: '' }
                }
                onChange={(selectedOption: any) =>
                  setSelectedCollection(selectedOption.value)
                }
              />
            </SelectWrapper>
          </Column>
        </FirstRow>
        <ScrollArea>
          {!popularColors.length ? null : (
            <PopularSection>
              <Title>{t('Popular colors')}</Title>
              <PopularFlexWrap>
                {popularColors.map((color: IColor, index: number) => {
                  const isSelected = getIsColorSelected(color, chosenColor);
                  return (
                    <PopularColorCard
                      isSelected={isSelected}
                      key={`popular-${index}-${color.name}`}
                      color={color}
                      handleChooseColor={handleChooseColor}
                    />
                  );
                })}
              </PopularFlexWrap>
            </PopularSection>
          )}
          <Title>{t('Results')}</Title>
          <ResultsSection>
            <GridWrap>
              {colors.map((color: IColor, index: number) => {
                const isSelected = getIsColorSelected(color, chosenColor);
                return (
                  <ColorCard
                    isSelected={isSelected}
                    key={`${index}-${color.name}`}
                    color={color}
                    handleChooseColor={handleChooseColor}
                  />
                );
              })}
            </GridWrap>
          </ResultsSection>
        </ScrollArea>
      </Container>
    </Modal>
  );
}
